
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Constituency, getConstituencyPartFromCode } from './types';
import { Country } from '@/contexts/country/types';

interface ConstituencyTableProps {
  constituencies: Constituency[];
  loading: boolean;
  availableCountries: Country[];
  onEdit: (constituency: Constituency) => void;
  onDelete: (id: string) => void;
  onToggleActive: (constituency: Constituency) => void;
  getCountryName: (code: string) => string;
}

const ConstituencyTable = ({
  constituencies,
  loading,
  availableCountries,
  onEdit,
  onDelete,
  onToggleActive,
  getCountryName,
}: ConstituencyTableProps) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (constituencies.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No constituencies found. Create your first constituency to get started.
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Country</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {constituencies.map((constituency) => (
            <TableRow key={constituency.id}>
              <TableCell className="font-medium">
                {constituency.name}
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="font-mono text-sm">{constituency.code}</div>
                  <div className="text-xs text-gray-500">
                    Part: {getConstituencyPartFromCode(constituency.code)}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {getCountryName(constituency.country_code)}
              </TableCell>
              <TableCell>
                <Badge
                  variant={constituency.active ? 'default' : 'secondary'}
                  className={
                    constituency.active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }
                >
                  {constituency.active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleActive(constituency)}
                    className="h-8 w-8 p-0"
                  >
                    {constituency.active ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(constituency)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(constituency.id)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ConstituencyTable;
